import { createColumnHelper } from '@tanstack/react-table';
import { Typography } from '@mui/material';
import { dateFromString, formatCurrency } from '@utils/utils.ts';
import { BillingHistory } from '@app/types/payment/billing';
import { TableMetaCustom } from '@app/types/overrides/table-meta';
import Tooltip from '@mui/material/Tooltip';
import AnimateButton from '@components/extended/AnimateButton';
import IconButton from '@mui/material/IconButton';
import { IconDownload } from '@tabler/icons-react';
import { billingDocumentFor } from '@features/settings-billing/model/billing-rows';

const columnHelper = createColumnHelper<BillingHistory>();

const billingHistoryTableColumns = [
  columnHelper.accessor('date', {
    id: 'date',
    header: 'Date',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">
        {dateFromString(new Date(original.date))}
      </Typography>
    ),
  }),
  columnHelper.accessor('description', {
    id: 'description',
    header: 'Plan Description',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">{original.description}</Typography>
    ),
  }),
  columnHelper.accessor('period', {
    id: 'period',
    header: 'Billing Period',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">{original.period}</Typography>
    ),
  }),
  columnHelper.accessor('amount', {
    id: 'amount',
    header: 'Total Amount',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => {
      return (
        <Typography variant="subtitle1">
          {formatCurrency(original.amount)}
        </Typography>
      );
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Payment Status',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">{original.status}</Typography>
    ),
  }),
  columnHelper.accessor('invoice_number', {
    id: 'invoice_number',
    header: 'Invoice Number',
    enableSorting: true,
    size: 100,
    enableResizing: false,
    cell: ({ row: { original } }) => (
      <Typography variant="body2">{original.invoice_number}</Typography>
    ),
  }),
  columnHelper.display({
    id: 'id',
    header: '',
    size: 20,
    cell: ({
      row: { original },
      table: {
        options: { meta },
      },
    }) => {
      const tableMeta = meta as TableMetaCustom<typeof original>;
      const doc = billingDocumentFor(original);
      // Nothing to open when Stripe issued nothing for the row.
      if (!doc) return null;
      const label =
        doc.kind === 'invoice' ? 'Download invoice' : 'View receipt';
      return (
        <div className="flex items-center justify-center gap-3 pe-4">
          <AnimateButton>
            <Tooltip title={label}>
              <IconButton
                size="small"
                aria-label={label}
                onClick={() => {
                  tableMeta?.handleDownloadRow &&
                    tableMeta?.handleDownloadRow(original);
                }}
              >
                <IconDownload />
              </IconButton>
            </Tooltip>
          </AnimateButton>
        </div>
      );
    },
  }),
];

export default billingHistoryTableColumns;
